import CreationToolUserRestriction from './user-restriction'

/**
 * Fields available for Super Cut capture and segment editing.
 */
enum CreationToolSuperCutField {
    CaptureTitle = 'CT_SUPER_CUT_FIELD_CAPTURE_TITLE',
    Watermark = 'CT_SUPER_CUT_FIELD_WATERMARK',
    WatermarkFile = 'CT_SUPER_CUT_FIELD_WATERMARK_FILE',
    WatermarkPreview = 'CT_SUPER_CUT_FIELD_WATERMARK_PREVIEW',
    WatermarkPosition = 'CT_SUPER_CUT_FIELD_WATERMARK_POSITION',
    Font = 'CT_SUPER_CUT_FIELD_FONT',
    FontFile = 'CT_SUPER_CUT_FIELD_FONT_FILE',
    FontPreview = 'CT_SUPER_CUT_FIELD_FONT_PREVIEW',
    Crop = 'CT_SUPER_CUT_FIELD_CROP',
    Caption = 'CT_SUPER_CUT_FIELD_CAPTION',
    CaptionPosition = 'CT_SUPER_CUT_FIELD_CAPTION_POSITION',
    CaptionStyle = 'CT_SUPER_CUT_FIELD_CAPTION_STYLE',
    SegmentationType = 'CT_SUPER_CUT_FIELD_SEGMENTATION_TYPE',
    SegmentaionTiming = 'CT_SUPER_CUT_FIELD_SEGMENTATION_TIMING',
    AutoCaption = 'CT_SUPER_CUT_FIELD_AUTO_CAPTION',
    CaptionLanguage = 'CT_SUPER_CUT_FIELD_CAPTION_LANGUAGE',
    AutoCrop = 'CT_SUPER_CUT_FIELD_AUTO_CROP',
}

namespace CreationToolSuperCutField {
    /**
     * Returns all fields (in order) for a specified type (capture or segment)
     */
    export function all(type: 'capture' | 'segment'): CreationToolSuperCutField[] {
        switch (type) {
            case 'capture':
                return [
                    CreationToolSuperCutField.CaptureTitle,
                    CreationToolSuperCutField.Caption,
                    CreationToolSuperCutField.AutoCaption,
                    CreationToolSuperCutField.CaptionLanguage,
                    CreationToolSuperCutField.Watermark,
                    CreationToolSuperCutField.WatermarkFile,
                    CreationToolSuperCutField.WatermarkPreview,
                    CreationToolSuperCutField.WatermarkPosition,
                    CreationToolSuperCutField.Font,
                    CreationToolSuperCutField.FontFile,
                    CreationToolSuperCutField.FontPreview,
                    CreationToolSuperCutField.Crop,
                    CreationToolSuperCutField.SegmentationType,
                    CreationToolSuperCutField.SegmentaionTiming,
                    CreationToolSuperCutField.CaptionPosition,
                    CreationToolSuperCutField.CaptionStyle,
                    CreationToolSuperCutField.AutoCrop,
                ]
            case 'segment':
                return []
        }
    }

    /**
     * Returns the field's title.
     */
    export function title(field: CreationToolSuperCutField): string {
        switch (field) {
            case CreationToolSuperCutField.CaptureTitle:
                return 'Capture Title'
            case CreationToolSuperCutField.Caption:
                return 'Captions'
            case CreationToolSuperCutField.Watermark:
                return 'Watermark'
            case CreationToolSuperCutField.WatermarkFile:
                return 'Upload New'
            case CreationToolSuperCutField.WatermarkPreview:
                return 'Previously Used'
            case CreationToolSuperCutField.WatermarkPosition:
                return 'Pick a Corner'
            case CreationToolSuperCutField.Font:
                return 'Custom Font'
            case CreationToolSuperCutField.FontFile:
                return 'Upload New'
            case CreationToolSuperCutField.FontPreview:
                return 'Previously Used'
            case CreationToolSuperCutField.Crop:
                return 'Default Crop'
            case CreationToolSuperCutField.SegmentationType:
                return 'Segmentation Type'
            case CreationToolSuperCutField.SegmentaionTiming:
                return 'Segmentation Timing'
            case CreationToolSuperCutField.CaptionPosition:
                return 'Default Caption Position'
            case CreationToolSuperCutField.CaptionStyle:
                return 'Default Caption Style'
            case CreationToolSuperCutField.AutoCaption:
                return 'Auto Caption'
            case CreationToolSuperCutField.CaptionLanguage:
                return 'Caption Language'
            case CreationToolSuperCutField.AutoCrop:
                return 'Auto Crop'
        }
    }

    /**
     * Returns the field's description text.
     */
    export function description(field: CreationToolSuperCutField): string {
        switch (field) {
            case CreationToolSuperCutField.CaptureTitle:
                return 'Capture Title'
            default:
                return ''
        }
    }

    /**
     * Returns the field's tool-tip helper text.
     */
    export function tooltipText(field: CreationToolSuperCutField): string {
        switch (field) {
            case CreationToolSuperCutField.Caption:
                return 'Uploading a subtitle track improves Super Cut’s scene detection – if you have one, upload it!'
            case CreationToolSuperCutField.Watermark:
                return 'Upload a watermark to attach branding to your clips. This can be changed later.'
            case CreationToolSuperCutField.Font:
                return 'Upload a custom font to use for captioning. This can be changed later.'
            case CreationToolSuperCutField.AutoCaption:
                return 'Auto Caption converts your subtitle track into captions for each scene.'
            case CreationToolSuperCutField.AutoCrop:
                return 'Automatically detect and remove black bars from horizontal and vertical content.'
            default:
                return ''
        }
    }

    /**
     * Returns the field's tool-tip helper text.
     */
    export function learnMoreUrl(field: CreationToolSuperCutField): string | null {
        switch (field) {
            default:
                return null
        }
    }

    /**
     * Returns a user restriction for the field.
     */
    export function userRestriction(field: CreationToolSuperCutField): CreationToolUserRestriction {
        switch (field) {
            case CreationToolSuperCutField.AutoCrop:
                return CreationToolUserRestriction.StaffOnly
            default:
                return CreationToolUserRestriction.Everyone
        }
    }

    /**
     * Returns whether or not a Capture field is editable after the Capture has already been cut.
     */
    export function isEditable(field: CreationToolSuperCutField): boolean {
        switch (field) {
            case CreationToolSuperCutField.Caption:
            case CreationToolSuperCutField.Crop:
            case CreationToolSuperCutField.AutoCaption:
            case CreationToolSuperCutField.CaptionLanguage:
            case CreationToolSuperCutField.AutoCrop:
                return false
            default:
                return true
        }
    }

    /**
     * Returns the max character count for the field, if it has one.
     */
    export function maxCharacterCount(field: CreationToolSuperCutField): number {
        switch (field) {
            case CreationToolSuperCutField.CaptureTitle:
                return 50
            default:
                return 0
        }
    }

    /**
     * Returns the prefix for this field's S3 key when uploading to DVR's S3 bucket.
     */
    export function s3BucketPrefix(field: CreationToolSuperCutField): string | null {
        switch (field) {
            case CreationToolSuperCutField.WatermarkFile:
                return 'watermarks'
            case CreationToolSuperCutField.FontFile:
                return 'fonts'
            case CreationToolSuperCutField.Caption:
                return 'captions'
            default:
                return null
        }
    }
}

export default CreationToolSuperCutField
